import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { ApiService } from '../../services/api';
import {
  ClassificationType,
  CropEmbeddingSearchResponse,
  EmbeddingSearchParams,
  EmbeddingSelectionMode,
} from '../../types';
import { EmbeddingTile } from './components/EmbeddingTile';
import { EmbeddingTypeFilter } from './components/EmbeddingTypeFilter';

const EmbeddingDetailPage: React.FC = () => {
  const { logout, token } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showInfoToast } = useNotificationContext();

  // Filters - initialize from query params
  const [chainId, setChainId] = useState<number | ''>(() => {
    const param = searchParams.get('chain_id');
    return param ? Number(param) : '';
  });
  const [storeId, setStoreId] = useState<string>(() => {
    return searchParams.get('store_id') || '';
  });
  const [upc, setUpc] = useState<string>(() => {
    return searchParams.get('upc') || '';
  });

  // Data
  const [embeddings, setEmbeddings] = useState<CropEmbeddingSearchResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filterType, setFilterType] = useState<ClassificationType | 'all'>('all');

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rejectedIds, setRejectedIds] = useState<Set<string>>(new Set());
  const [selectingMode, setSelectingMode] = useState<EmbeddingSelectionMode | null>(null);

  // Save state
  const [saving, setSaving] = useState(false);

  // Refresh trigger
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    if (selectingMode === EmbeddingSelectionMode.ACTIVATING) {
      showInfoToast('Select all embeddings to activate');
    } else if (selectingMode === EmbeddingSelectionMode.DEACTIVATING) {
      showInfoToast('Select all embeddings to deactivate');
    }
  }, [selectingMode, showInfoToast]);

  // Fetch embeddings when query params change
  useEffect(() => {
    const chainIdParam = searchParams.get('chain_id');
    const storeIdParam = searchParams.get('store_id');
    const upcParam = searchParams.get('upc');

    // Only fetch if required params are present
    if (!token || !chainIdParam || !upcParam) {
      setEmbeddings([]);
      return;
    }

    const fetchEmbeddings = async () => {
      setLoading(true);
      setError(null);

      try {
        const params: EmbeddingSearchParams = {
          chainId: Number(chainIdParam),
          extendedSearch: true,
          normalizedUpcs: upcParam,
          limit: 100,
          ...(storeIdParam && { storeId: Number(storeIdParam) }),
        };

        const embeddings = await ApiService.searchEmbeddings(token, params);

        setEmbeddings(embeddings);
      } catch (err) {
        console.error('Error fetching embeddings:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch embeddings');
        setEmbeddings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEmbeddings();
  }, [token, searchParams, refreshTrigger]);

  // Handle search button click - update URL query params
  const handleSearch = () => {
    const params: Record<string, string> = {};

    if (chainId) {
      params.chain_id = String(chainId);
    }
    if (storeId) {
      params.store_id = storeId;
    }
    if (upc) {
      params.upc = upc;
    }

    setSearchParams(params);
  };

  // Handle Enter key press in input fields
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && chainId && upc) {
      handleSearch();
    }
  };

  // Handle checkbox toggle
  const handleCheckboxChange = ({
    productEmbeddingCropId: embeddingId,
    isActive,
    isPending,
  }: CropEmbeddingSearchResponse) => {
    if (!embeddingId) {
      return;
    }

    if (rejectedIds.has(embeddingId)) {
      setRejectedIds((prev) => {
        const newSet = new Set(prev);

        newSet.delete(embeddingId);

        if (newSet.size === 0 && selectedIds.size === 0) {
          setSelectingMode(null);
        }

        return newSet;
      });

      return;
    }

    if (isPending && selectingMode === EmbeddingSelectionMode.DEACTIVATING) {
      setRejectedIds((prev) => {
        const newSet = new Set(prev);
        newSet.add(embeddingId);

        return newSet;
      });
      return;
    }

    setSelectedIds((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(embeddingId)) {
        newSet.delete(embeddingId);

        if (newSet.size === 0 && rejectedIds.size === 0) {
          setSelectingMode(null);
        }

        return newSet;
      }

      // If this is the first selection, store its active state
      if (prev.size === 0 && rejectedIds.size === 0) {
        setSelectingMode(
          isActive ? EmbeddingSelectionMode.DEACTIVATING : EmbeddingSelectionMode.ACTIVATING
        );
        newSet.add(embeddingId);
        return newSet;
      }

      if (rejectedIds.size > 0) {
        // When rejecting, can only select active items (for deactivation)
        if (isActive) {
          newSet.add(embeddingId);
        }

        return newSet;
      }

      // Only allow selection if the item matches the first selected state
      const expectedMode = isActive
        ? EmbeddingSelectionMode.DEACTIVATING
        : EmbeddingSelectionMode.ACTIVATING;

      if (selectingMode === expectedMode) {
        newSet.add(embeddingId);
      }

      return newSet;
    });
  };

  const handleReject = (embeddingId: string) => {
    if (selectingMode === EmbeddingSelectionMode.ACTIVATING) {
      return;
    }

    setRejectedIds((prev) => {
      const newSet = new Set(prev);
      newSet.add(embeddingId);

      // When starting rejection with no other selections, set to deactivate mode
      if (selectedIds.size === 0 && selectingMode === null) {
        setSelectingMode(EmbeddingSelectionMode.DEACTIVATING);
      }

      return newSet;
    });
  };

  // Handle save
  const handleSave = async () => {
    if (!token || selectingMode === null || (selectedIds.size === 0 && rejectedIds.size === 0)) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Update selected embeddings (toggle active state)
      if (selectedIds.size > 0 || rejectedIds.size) {
        const newActiveState = selectingMode === EmbeddingSelectionMode.ACTIVATING;
        await ApiService.updateEmbeddingsActiveState(
          token,
          [...selectedIds, ...rejectedIds],
          newActiveState
        );
      }

      // Clear selections and trigger refresh
      setSelectedIds(new Set());
      setRejectedIds(new Set());
      setSelectingMode(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      console.error('Error saving embeddings:', err);
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleFilterChange = (value: ClassificationType | 'all') => {
    setFilterType(value);
  };

  // Filter embeddings based on classification type
  const filteredEmbeddings = useMemo(() => {
    if (filterType === 'all') {
      return embeddings;
    }
    return embeddings.filter((embedding) => embedding.classificationType === filterType);
  }, [embeddings, filterType]);

  // Get product details from first row
  const productDetails = embeddings.length > 0 ? embeddings[0] : null;

  const pendingEmbeddings = useMemo(
    () => filteredEmbeddings.filter(({ isPending }) => isPending),
    [filteredEmbeddings]
  );

  const notPendingEmbeddings = useMemo(
    () =>
      filteredEmbeddings
        .filter(({ isPending }) => !isPending)
        .sort((a, b) => {
          if (a.isActive === b.isActive) {
            return 0;
          }

          return a.isActive ? -1 : 1;
        }),
    [filteredEmbeddings]
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start justify-between">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Embedding Details</h1>
          <button
            onClick={() => logout()}
            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-md"
          >
            Sign Out
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chain ID <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={chainId}
                onChange={(e) => setChainId(e.target.value ? Number(e.target.value) : '')}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter chain ID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Store ID</label>
              <input
                type="number"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter store ID (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                UPC <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={upc}
                onChange={(e) => setUpc(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter UPC"
              />
            </div>
          </div>

          <button
            onClick={handleSearch}
            disabled={!chainId || !upc || loading}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : 'Search'}
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Empty states */}
        {!loading && !chainId && !upc && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 text-lg">
              Please enter a Chain ID and UPC to view embeddings
            </p>
          </div>
        )}

        {!loading && chainId && upc && embeddings.length === 0 && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 text-lg">No embeddings found for this UPC</p>
          </div>
        )}

        {/* Main content - Two column layout */}
        {!loading && embeddings.length > 0 && productDetails && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column - Product details */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow p-6 sticky top-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Product Details</h2>

                {/* Product image */}
                {productDetails.productImage && (
                  <div className="mb-4">
                    <img
                      src={productDetails.productImage}
                      alt="Product"
                      className="w-full h-auto rounded-lg border border-gray-200"
                    />
                  </div>
                )}

                {/* Product info */}
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Product Name</p>
                    <p className="text-base font-medium text-gray-900">
                      {productDetails.productName || 'N/A'}
                    </p>
                  </div>

                  {productDetails.itemNumber && (
                    <div>
                      <p className="text-sm text-gray-500">Item number</p>
                      <p className="text-base font-medium text-gray-900">
                        {productDetails.itemNumber}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm text-gray-500">Normalized UPC</p>
                    <p className="text-base font-medium text-gray-900">
                      {productDetails.normalizedUpc}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Barcode</p>
                    <p className="text-base font-medium text-gray-900">{productDetails.barcode}</p>
                  </div>
                  <a
                    href={`https://www.google.com/search?q=${productDetails.barcode}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    Search on Google
                  </a>
                </div>
              </div>
            </div>

            {/* Right column - Embeddings grid */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow p-6">
                {/* Grid of images */}
                <div className="space-y-8">
                  {/* Pending Embeddings Section */}
                  {pendingEmbeddings.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold text-gray-900">
                          Pending Embeddings ({pendingEmbeddings.length})
                        </h2>
                        <EmbeddingTypeFilter value={filterType} onChange={handleFilterChange} />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-8">
                        {pendingEmbeddings.map((embedding) => {
                          const isRejected = rejectedIds.has(
                            embedding.productEmbeddingCropId || ''
                          );
                          const isSelected = selectedIds.has(
                            embedding.productEmbeddingCropId || ''
                          );

                          const canReject = selectingMode !== EmbeddingSelectionMode.ACTIVATING;

                          return (
                            <EmbeddingTile
                              canReject={canReject}
                              selectingMode={selectingMode}
                              isSelected={isSelected || isRejected}
                              onReject={handleReject}
                              isDisabled={false}
                              embedding={embedding}
                              onCheckboxChange={handleCheckboxChange}
                            ></EmbeddingTile>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Other Embeddings Section */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold text-gray-900">
                        Embeddings ({notPendingEmbeddings.length})
                      </h2>
                      {pendingEmbeddings.length === 0 && (
                        <EmbeddingTypeFilter value={filterType} onChange={handleFilterChange} />
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-8">
                      {notPendingEmbeddings.map((embedding) => {
                        const isSelected = selectedIds.has(embedding.productEmbeddingCropId || '');
                        const isDisabled =
                          (selectingMode !== null &&
                            ((embedding.isActive &&
                              selectingMode === EmbeddingSelectionMode.ACTIVATING) ||
                              (!embedding.isActive &&
                                selectingMode === EmbeddingSelectionMode.DEACTIVATING))) ||
                          (rejectedIds.size > 0 && !embedding.isActive);

                        return (
                          <EmbeddingTile
                            canReject={false}
                            selectingMode={selectingMode}
                            isSelected={isSelected}
                            isDisabled={isDisabled}
                            embedding={embedding}
                            onCheckboxChange={handleCheckboxChange}
                          ></EmbeddingTile>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                {(selectedIds.size > 0 || rejectedIds.size > 0) && (
                  <div className="mt-6 pt-6 border-t border-gray-200 flex justify-between items-center">
                    <button
                      onClick={() => {
                        setSelectedIds(new Set());
                        setRejectedIds(new Set());
                        setSelectingMode(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Clear Selection
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className={`px-6 py-3 text-white rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed font-medium ${
                        selectingMode === EmbeddingSelectionMode.DEACTIVATING
                          ? 'bg-red-600 hover:bg-red-700'
                          : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {saving
                        ? 'Saving...'
                        : `Set ${selectedIds.size + rejectedIds.size} as ${
                            selectingMode === EmbeddingSelectionMode.DEACTIVATING
                              ? 'Inactive'
                              : 'Active'
                          }`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmbeddingDetailPage;
